import { computed } from 'vue';
import { DynamicCanvasSizes } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';

export type DescriptionPosition = 'top' | 'middle' | 'bottom';

interface UseWindowDescriptionsOptions {
    /** Localized "top-down view" label (typically `proxy.$t('Top-down View')`). */
    topViewDescription: () => string;
    /** Localized "view from the inside out" label. */
    windowDescription: () => string;
    /** Per-product dynamic sizing — supplies the description Y offset. */
    canvasTemplateConfiguration: () => DynamicCanvasSizes;
}

/**
 * Builds the Konva text & background configs for the window description
 * labels rendered on the first window of a combination, plus the vertical
 * offset for the bottom description group.
 *
 * Shared across canvas layouts so localized strings and styling stay in sync.
 */
export function useWindowDescriptions(options: UseWindowDescriptionsOptions) {
    const { topViewDescription, windowDescription, canvasTemplateConfiguration } = options;

    const descriptionYOffset = computed<number>(() => {
        return canvasTemplateConfiguration().descriptionYOffset ?? 110;
    });

    function resolveDescription(position: DescriptionPosition): string {
        switch (position) {
            case 'top':
                return topViewDescription();
            case 'middle':
                return windowDescription();
            case 'bottom':
            default:
                return '';
        }
    }

    function calculateTextWidth(text: string): number {
        const pixelsPerChar = 20;
        const calculatedWidth = text.length * pixelsPerChar;
        const maxWidth = 800;
        return Math.min(calculatedWidth, maxWidth);
    }

    function descriptionTextConfig(position: DescriptionPosition) {
        const description = resolveDescription(position);
        const textWidth = calculateTextWidth(description);

        return {
            x: 20,
            y: 20,
            text: description,
            fontSize: 35,
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontStyle: 'normal',
            fill: '#797979',
            align: 'center',
            verticalAlign: 'middle',
            width: textWidth,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            wrap: 'none',
        };
    }

    function textBackgroundConfig(position: DescriptionPosition) {
        const description = resolveDescription(position);
        const textWidth = calculateTextWidth(description);
        const horizontalPadding = 40;

        return {
            x: 0,
            y: 0,
            width: textWidth + horizontalPadding,
            height: 72,
            fill: '#f5f5f5',
            stroke: '#e0e0e0',
            strokeWidth: 1,
            cornerRadius: 6,
            shadowColor: 'rgba(0, 0, 0, 0.08)',
            shadowBlur: 4,
            shadowOffset: { x: 0, y: 2 },
            shadowOpacity: 1,
            perfectDrawEnabled: true,
        };
    }

    return {
        descriptionYOffset,
        descriptionTextConfig,
        textBackgroundConfig,
    };
}
